import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";

import { getReservationByToken, cancelReservationByToken } from "../actions/reservationActions";
import { t } from "../i18n/translations";
import Loader from "../components/Loader";
import Message from "../components/Message";

import "./ReservationLookupScreen.css";

const CANCELLABLE_STATUSES = ["pending", "confirmed"];

const STATUS_KEYS = {
  pending: "reservationStatusPending",
  confirmed: "reservationStatusConfirmed",
  seated: "reservationStatusSeated",
  completed: "reservationStatusCompleted",
  cancelled: "reservationStatusCancelled",
  no_show: "reservationStatusNoShow",
};

function ReservationLookupScreen() {
  const { token } = useParams();
  const dispatch = useDispatch();

  const { language } = useSelector((state) => state.language);
  const { loading, error, reservation } = useSelector((state) => state.reservationLookup || {});
  const { loading: cancelling, success: cancelSuccess } = useSelector(
    (state) => state.reservationCancel || {},
  );

  useEffect(() => {
    dispatch(getReservationByToken(token));
  }, [dispatch, token]);

  useEffect(() => {
    if (cancelSuccess) dispatch(getReservationByToken(token));
  }, [cancelSuccess, dispatch, token]);

  const cancelHandler = () => {
    if (window.confirm(t("cancelReservationConfirm", language))) {
      dispatch(cancelReservationByToken(token));
    }
  };

  if (loading) return <Loader />;
  if (error) return <Message variant="danger">{t("reservationNotFound", language)}</Message>;
  if (!reservation) return null;

  return (
    <div className="home-container reservation-lookup">
      <h1 className="events-page-title">{t("reservationLookupTitle", language)}</h1>

      <div className="reservation-lookup-card">
        <div className="reservation-lookup-row">
          <span>{t("guestNameLabel", language)}</span>
          <strong>{reservation.guest_name}</strong>
        </div>

        {reservation.event && (
          <div className="reservation-lookup-row">
            <span>🎉</span>
            <strong>{reservation.event.title}</strong>
          </div>
        )}

        <div className="reservation-lookup-row">
          <span>{t("reservationDateTimeLabel", language)}</span>
          <strong>
            {new Date(reservation.date).toLocaleDateString(
              language === "hu" ? "hu-HU" : language,
              { month: "long", day: "numeric" },
            )}{" "}
            · {reservation.time?.slice(0, 5)}
          </strong>
        </div>

        <div className="reservation-lookup-row">
          <span>{t("reservationPartySizeLabel", language)}</span>
          <strong>{reservation.party_size}</strong>
        </div>

        {reservation.guest_note && (
          <div className="reservation-lookup-row">
            <span>{t("guestNoteLabel", language)}</span>
            <strong>{reservation.guest_note}</strong>
          </div>
        )}

        <div className="reservation-lookup-row">
          <span>{t("reservationStatusLabel", language)}</span>
          <strong className={`reservation-status-pill status-${reservation.status}`}>
            {t(STATUS_KEYS[reservation.status] || "reservationStatusPending", language)}
          </strong>
        </div>
      </div>

      {CANCELLABLE_STATUSES.includes(reservation.status) && (
        <button
          type="button"
          className="reservation-cancel-btn"
          onClick={cancelHandler}
          disabled={cancelling}
        >
          {t("cancelReservationButton", language)}
        </button>
      )}

      {reservation.status === "cancelled" && (
        <Message variant="warning">{t("reservationCancelledMessage", language)}</Message>
      )}

      <div className="text-center mt-4">
        <Link to="/">{t("backToMenu", language)}</Link>
      </div>
    </div>
  );
}

export default ReservationLookupScreen;
